import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Image,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { relationshipsService, RelationshipUser } from '../../services/relationships';
import { accessGrantsService } from '../../services/accessGrants';
import { accountantInfoService } from '../../services/accountantInfo';
import { attorneyInfoService } from '../../services/attorneyInfo';
import { investmentAdvisorInfoService } from '../../services/investmentAdvisorInfo';
import { insuranceAgentInfoService } from '../../services/insuranceAgentInfo';
import { taxAdvisorInfoService } from '../../services/taxAdvisorInfo';
import { creditCardInfoService } from '../../services/creditCardInfo';
import { bankingInfoService } from '../../services/bankingInfo';
import { investmentsInfoService } from '../../services/investmentsInfo';
import { loanInfoService } from '../../services/loanInfo';
import { cryptoInfoService } from '../../services/cryptoInfo';
import { homeownersInsuranceInfoService } from '../../services/homeownersInsuranceInfo';
import { cellphoneProviderInfoService } from '../../services/cellphoneProviderInfo';
import { gasProviderInfoService } from '../../services/gasProviderInfo';
import { homeOwnershipInfoService } from '../../services/homeOwnershipInfo';
import { specialtyPhysicianInfoService } from '../../services/specialtyPhysicianInfo';
import { primaryPhysicianInfoService } from '../../services/primaryPhysicianInfo';
import { medicalHistoryInfoService } from '../../services/medicalHistoryInfo';
import { medicationService } from '../../services/medication';
import { caregiverInfoService } from '../../services/caregiverInfo';
import { medicalEquipmentService } from '../../services/medicalEquipment';
import { organDonorInfoService } from '../../services/organDonorInfo';
import { treatmentsService } from '../../services/treatments';

import { createLogger } from '../../utils/logger';
const logger = createLogger('AccessControlCard');

interface AccessControlCardProps {
  dataType: string;
  dataId: string | string[]; // Now accepts single ID or array of IDs for bulk operations
  onComplete: (selectedEmails: string[], lockStatus: 'locked' | 'unlocked') => void;
  onCancel: () => void;
}

// Map dataType to task key
const getTaskKeyFromDataType = (dataType: string): string => {
  const normalized = dataType.toLowerCase().trim();
  const mapping: Record<string, string> = {
    'medical history': 'medical_history',
    'medical': 'medical_history',
    'medication': 'current_medications',
    'medications': 'current_medications',
    'current medications': 'current_medications',
    'primary physician': 'primary_physician_info',
    'physician': 'primary_physician_info',
    'specialty physician': 'specialty_physicians',
    'specialty physicians': 'specialty_physicians',
    'caregiver': 'caregiver_info',
    'caregivers': 'caregiver_info',
    'caregiver information': 'caregiver_info',
    'medical equipment': 'medical_equipment',
    'equipment': 'medical_equipment',
    'organ donation': 'organ_donation',
    'organ donor': 'organ_donation',
    'current therapy': 'current_therapy',
    'therapy': 'current_therapy',
    'treatment': 'current_therapy',
    'treatments': 'current_therapy',
    'current treatment': 'current_therapy',
    'beneficiary': 'beneficiary',
    'beneficiaries': 'beneficiary',
    'life insurance': 'life_insurance',
    'insurance': 'life_insurance',
    'banking': 'banking',
    'bank account': 'banking',
    'attorney': 'attorney',
    'attorney information': 'attorney',
    'accountant': 'accountant',
    'accountant information': 'accountant',
    'investment advisor': 'investment_advisor',
    'investment advisor information': 'investment_advisor',
    'insurance agent': 'insurance_agent',
    'insurance agent information': 'insurance_agent',
    'tax advisor': 'tax_advisor',
    'tax advisor information': 'tax_advisor',
    'credit card': 'credit_cards',
    'credit cards': 'credit_cards',
    'investment account': 'investments',
    'investment accounts': 'investments',
    'investments': 'investments',
    'loan': 'loans',
    'loans': 'loans',
    'crypto': 'crypto',
    'crypto currency': 'crypto',
    'cryptocurrency': 'crypto',
    'funeral home': 'funeral_home',
    'burial preferences': 'burial_preferences',
    'burial': 'burial_preferences',
    'personal info': 'personal_info',
    'personal information': 'personal_info',
    'homeowners insurance': 'homeowners_insurance',
    'homeowner insurance': 'homeowners_insurance',
    'home insurance': 'homeowners_insurance',
    'cellphone provider': 'cellphone',
    'cellphone': 'cellphone',
    'cell phone': 'cellphone',
    'mobile provider': 'cellphone',
    'gas provider': 'gas_utility',
    'gas utility': 'gas_utility',
    'gas': 'gas_utility',
    'home ownership': 'home_ownership',
    'living arrangements': 'home_ownership',
    'living arrangement': 'home_ownership',
    'information': 'information',
  };
  return mapping[normalized] || normalized.replace(/ /g, '_');
};

export const AccessControlCard: React.FC<AccessControlCardProps> = ({
  dataType,
  dataId,
  onComplete,
  onCancel,
}) => {
  // Validate props
  if (!dataType || !dataId || typeof dataType !== 'string' || typeof dataId !== 'string') {
    logger.error('Invalid AccessControlCard props:', { dataType, dataId });
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Invalid data. Please try again.</Text>
      </View>
    );
  }

  const [successors, setSuccessors] = useState<RelationshipUser[]>([]);
  const [keyholders, setKeyholders] = useState<RelationshipUser[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [lockChoice, setLockChoice] = useState<'locked' | 'unlocked' | null>(null);

  useEffect(() => {
    loadRelationships();
  }, []);

  const loadRelationships = async () => {
    try {
      setLoading(true);
      const data = await relationshipsService.getRelationships();
      setSuccessors(data.successors || []);
      setKeyholders(data.keyholders || []);
    } catch (error) {
      logger.error('Error loading relationships:', error);
      Alert.alert('Error', 'Failed to load contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleContact = (contactId: string) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleLockChoice = async (choice: 'locked' | 'unlocked') => {
    setLockChoice(choice);
    if (choice === 'locked') {
      setLoading(true);
      try {
        const taskKey = getTaskKeyFromDataType(dataType);

        // Handle both single ID and array of IDs for bulk operations
        const ids = Array.isArray(dataId) ? dataId : [dataId];
        logger.debug(`[AccessControlCard] Locking ${ids.length} ${taskKey} entries`);

        // Lock each ID
        for (const id of ids) {
          // Use entity-specific lock service if available
          if (taskKey === 'accountant') {
            await accountantInfoService.lockAccountantInfo(id);
          } else if (taskKey === 'attorney') {
            await attorneyInfoService.lockAttorneyInfo(id);
          } else if (taskKey === 'investment_advisor') {
            await investmentAdvisorInfoService.lockInvestmentAdvisorInfo(id);
          } else if (taskKey === 'insurance_agent') {
            await insuranceAgentInfoService.lockInsuranceAgent(id);
          } else if (taskKey === 'tax_advisor') {
            await taxAdvisorInfoService.lockTaxAdvisorInfo(id);
          } else if (taskKey === 'credit_cards') {
            await creditCardInfoService.lockCreditCardInfo(id);
          } else if (taskKey === 'banking') {
            await bankingInfoService.lockBankingInfo(id);
          } else if (taskKey === 'investments') {
            await investmentsInfoService.lockInvestmentsInfo(id);
          } else if (taskKey === 'loans') {
            await loanInfoService.lockLoanInfo(id);
          } else if (taskKey === 'crypto') {
            await cryptoInfoService.lockCryptoInfo(id);
          } else if (taskKey === 'homeowners_insurance') {
            await homeownersInsuranceInfoService.lockHomeownersInsuranceInfo(id);
          } else if (taskKey === 'cellphone') {
            await cellphoneProviderInfoService.lockCellphoneProviderInfo(id);
          } else if (taskKey === 'gas_utility') {
            await gasProviderInfoService.lockGasProviderInfo(id);
          } else if (taskKey === 'home_ownership') {
            await homeOwnershipInfoService.lockHomeOwnershipInfo(id);
          } else if (taskKey === 'specialty_physicians') {
            await specialtyPhysicianInfoService.lockSpecialtyPhysicianInfo(id);
          } else if (taskKey === 'primary_physician_info') {
            await primaryPhysicianInfoService.lockPrimaryPhysicianInfo(id);
          } else if (taskKey === 'medical_history') {
            await medicalHistoryInfoService.lockMedicalHistoryInfo(id);
          } else if (taskKey === 'caregiver_info') {
            await caregiverInfoService.lockCaregiverInfo(id);
          } else if (taskKey === 'organ_donation') {
            await organDonorInfoService.lockOrganDonor(id);
          } else if (taskKey === 'current_therapy') {
            await treatmentsService.lockTreatment(id);
          } else if (taskKey === 'current_medications') {
            await medicationService.lockMedication(id);
          } else {
            // Fall back to generic service for other types
            const response = await accessGrantsService.lockAccess({
              taskKey,
              entityId: id,
            });

            if (!response.success) {
              Alert.alert('Error', response.error || 'Failed to lock access');
              return;
            }
          }
        }

        logger.debug(`[AccessControlCard] Successfully locked ${ids.length} entries`);
        onComplete([], 'locked');
      } catch (error) {
        logger.error('Error locking access:', error);
        Alert.alert('Error', 'Failed to lock access. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    // If unlocked, show the contact selection (already visible)
  };

  const handleSave = async () => {
    const allContacts = [...successors, ...keyholders];
    const selectedEmails = allContacts
      .filter((c) => selectedContacts.includes(c.id))
      .map((c) => c.email)
      .filter(Boolean);

    if (selectedEmails.length === 0) {
      Alert.alert('Error', 'Please select at least one contact');
      return;
    }

    setLoading(true);
    try {
      const taskKey = getTaskKeyFromDataType(dataType);

      // Handle both single ID and array of IDs for bulk operations
      const ids = Array.isArray(dataId) ? dataId : [dataId];
      logger.debug(`[AccessControlCard] Unlocking ${ids.length} ${taskKey} entries for ${selectedEmails.length} contacts`);

      // Unlock each ID for the selected contacts
      for (const id of ids) {
        // Use entity-specific unlock service if available
        if (taskKey === 'accountant') {
          await accountantInfoService.unlockAccountantInfo(id, selectedEmails);
        } else if (taskKey === 'attorney') {
          await attorneyInfoService.unlockAttorneyInfo(id, selectedEmails);
        } else if (taskKey === 'investment_advisor') {
          await investmentAdvisorInfoService.unlockInvestmentAdvisorInfo(id, selectedEmails);
        } else if (taskKey === 'insurance_agent') {
          await insuranceAgentInfoService.unlockInsuranceAgent(id, selectedEmails);
        } else if (taskKey === 'tax_advisor') {
          await taxAdvisorInfoService.unlockTaxAdvisorInfo(id, selectedEmails);
        } else if (taskKey === 'credit_cards') {
          await creditCardInfoService.unlockCreditCardInfo(id, selectedEmails);
        } else if (taskKey === 'banking') {
          await bankingInfoService.unlockBankingInfo(id, selectedEmails);
        } else if (taskKey === 'investments') {
          await investmentsInfoService.unlockInvestmentsInfo(id, selectedEmails);
        } else if (taskKey === 'loans') {
          await loanInfoService.unlockLoanInfo(id, selectedEmails);
        } else if (taskKey === 'crypto') {
          await cryptoInfoService.unlockCryptoInfo(id, selectedEmails);
        } else if (taskKey === 'homeowners_insurance') {
          await homeownersInsuranceInfoService.unlockHomeownersInsuranceInfo(id, selectedEmails);
        } else if (taskKey === 'cellphone') {
          await cellphoneProviderInfoService.unlockCellphoneProviderInfo(id, selectedEmails);
        } else if (taskKey === 'gas_utility') {
          await gasProviderInfoService.unlockGasProviderInfo(id, selectedEmails);
        } else if (taskKey === 'home_ownership') {
          await homeOwnershipInfoService.unlockHomeOwnershipInfo(id, selectedEmails);
        } else if (taskKey === 'specialty_physicians') {
          await specialtyPhysicianInfoService.unlockSpecialtyPhysicianInfo(id, selectedEmails);
        } else if (taskKey === 'primary_physician_info') {
          await primaryPhysicianInfoService.unlockPrimaryPhysicianInfo(id, selectedEmails);
        } else if (taskKey === 'medical_history') {
          await medicalHistoryInfoService.unlockMedicalHistoryInfo(id, selectedEmails);
        } else if (taskKey === 'caregiver_info') {
          await caregiverInfoService.unlockCaregiverInfo(id, selectedEmails);
        } else if (taskKey === 'organ_donation') {
          await organDonorInfoService.unlockOrganDonor(id, selectedEmails);
        } else if (taskKey === 'current_therapy') {
          await treatmentsService.unlockTreatment(id, selectedEmails);
        } else if (taskKey === 'current_medications') {
          await medicationService.unlockMedication(id, selectedEmails);
        } else {
          // Fall back to generic service for other types
          const response = await accessGrantsService.unlockAccess({
            taskKey,
            entityId: id,
            granteeEmails: selectedEmails,
          });

          if (!response.success) {
            Alert.alert('Error', response.error || 'Failed to unlock access');
            return;
          }
        }
      }

      logger.debug(`[AccessControlCard] Successfully unlocked ${ids.length} entries`);
      onComplete(selectedEmails, 'unlocked');
    } catch (error) {
      logger.error('Error unlocking access:', error);
      Alert.alert('Error', 'Failed to unlock access. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderContact = (contact: RelationshipUser) => {
    const isSelected = selectedContacts.includes(contact.id);
    const isPending = contact.status === 'pending';

    return (
      <TouchableOpacity
        key={contact.id}
        style={[styles.contactRow, isPending && styles.pendingContactRow]}
        onPress={() => toggleContact(contact.id)}
      >
        <View style={[styles.avatarPlaceholder, isPending && styles.pendingAvatar]}>
          <Text style={[styles.avatarText, isPending && styles.pendingAvatarText]}>
            {contact.name?.charAt(0)?.toUpperCase() || '?'}
          </Text>
        </View>
        <View style={styles.contactInfo}>
          <View style={styles.contactNameRow}>
            <Text style={styles.contactName}>{contact.name}</Text>
            {isPending && <Text style={styles.pendingLabel}>Pending</Text>}
          </View>
          <Text style={styles.contactEmail}>{contact.email}</Text>
        </View>
        <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
          {isSelected && (
            <Image
              source={require('../../assets/icons/check.png')}
              style={styles.checkIcon}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>Loading contacts...</Text>
        </View>
      </View>
    );
  }

  // Step 1: Lock/Unlock choice
  if (lockChoice === null) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Access Control</Text>
          <Text style={styles.subtitle}>
            Would you like to lock or unlock your {dataType}?
          </Text>
        </View>

        <View style={styles.explanationBox}>
          <Text style={styles.explanationText}>
            🔒 <Text style={styles.bold}>Locked:</Text> Only you can see this until you pass away
          </Text>
          <Text style={styles.explanationText}>
            🔓 <Text style={styles.bold}>Unlocked:</Text> Your selected people can view it right now
          </Text>
        </View>

        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.button, styles.lockButton]}
            onPress={() => handleLockChoice('locked')}
          >
            <Image
              source={require('../../assets/icons/BlueLock.png')}
              style={styles.buttonIcon}
            />
            <Text style={styles.buttonText}>Keep it Locked</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.button, styles.unlockButton]}
            onPress={() => handleLockChoice('unlocked')}
          >
            <Image
              source={require('../../assets/icons/BlueUnlock.png')}
              style={styles.buttonIcon}
            />
            <Text style={styles.buttonText}>Unlock & Share</Text>
          </TouchableOpacity>
        </View>

        <TouchableOpacity style={styles.cancelButtonStandalone} onPress={onCancel}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Step 2: Contact selection (only if unlocked)
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.unlockedBadge}>
          <Image
            source={require('../../assets/icons/BlueUnlock.png')}
            style={styles.unlockIconSmall}
          />
          <Text style={styles.unlockedText}>UNLOCKED</Text>
        </View>
        <Text style={styles.subtitle}>Select who can view this data</Text>
      </View>

      <ScrollView style={styles.scrollView} nestedScrollEnabled>
        {successors.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Successors</Text>
            {successors.map(renderContact)}
          </View>
        )}

        {keyholders.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Key Holders</Text>
            {keyholders.map(renderContact)}
          </View>
        )}

        {successors.length === 0 && keyholders.length === 0 && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateText}>No contacts found</Text>
            <Text style={styles.emptyStateSubtext}>
              Add successors or key holders first to share this information
            </Text>
          </View>
        )}
      </ScrollView>

      <View style={styles.actions}>
        <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.saveButton, selectedContacts.length === 0 && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={selectedContacts.length === 0}
        >
          <Text style={styles.saveButtonText}>
            Save ({selectedContacts.length} selected)
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    padding: ms(16),
    marginVertical: ms(8),
    marginHorizontal: 0,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
    maxHeight: s(500),
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: ms(40),
  },
  loadingText: {
    marginTop: ms(12),
    fontSize: ms(14, 0.3),
    color: '#666',
  },
  header: {
    marginBottom: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
    paddingBottom: ms(12),
  },
  title: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(4),
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    color: '#666',
  },
  explanationBox: {
    backgroundColor: '#F5F5F5',
    borderRadius: ms(8),
    padding: ms(12),
    marginBottom: ms(16),
  },
  explanationText: {
    fontSize: ms(14, 0.3),
    color: '#333',
    marginBottom: ms(8),
    lineHeight: ms(20, 0.3),
  },
  bold: {
    fontWeight: 'bold',
  },
  unlockedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(8),
  },
  unlockIconSmall: {
    width: s(24),
    height: s(24),
    marginRight: ms(8),
  },
  unlockedText: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#29BFC0',
  },
  scrollView: {
    maxHeight: s(250),
    marginBottom: ms(12),
  },
  section: {
    marginBottom: ms(16),
  },
  sectionTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(12),
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: ms(8),
    marginBottom: ms(8),
  },
  pendingContactRow: {
    opacity: 0.7,
  },
  avatarPlaceholder: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pendingAvatar: {
    backgroundColor: '#999',
  },
  avatarText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  pendingAvatarText: {
    color: '#FFFFFF',
  },
  contactInfo: {
    flex: 1,
    marginLeft: ms(12),
  },
  contactNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  contactName: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#333',
  },
  pendingLabel: {
    fontSize: ms(10, 0.3),
    color: '#FFFFFF',
    fontWeight: '600',
    marginLeft: ms(6),
    paddingHorizontal: ms(6),
    paddingVertical: ms(2),
    backgroundColor: '#F2994A',
    borderRadius: ms(8),
  },
  contactEmail: {
    fontSize: ms(12, 0.3),
    color: '#666',
    marginTop: ms(2),
  },
  checkbox: {
    width: s(20),
    height: s(20),
    borderRadius: ms(10),
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    marginLeft: ms(12),
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#29BFC0',
  },
  checkIcon: {
    width: s(10),
    height: s(10),
    tintColor: '#FFFFFF',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: ms(40),
  },
  emptyStateText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(8),
  },
  emptyStateSubtext: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: ms(8),
    marginTop: ms(12),
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: ms(12),
    borderRadius: ms(8),
    gap: ms(8),
  },
  lockButton: {
    backgroundColor: '#FF9F5A',
  },
  unlockButton: {
    backgroundColor: '#29BFC0',
  },
  buttonIcon: {
    width: s(20),
    height: s(20),
  },
  buttonText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: ms(12),
    borderRadius: ms(8),
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
  },
  cancelButtonStandalone: {
    paddingVertical: ms(10),
    paddingHorizontal: ms(24),
    borderRadius: ms(8),
    backgroundColor: 'transparent',
    alignItems: 'center',
    marginTop: ms(8),
    alignSelf: 'center',
  },
  cancelButtonText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#666',
  },
  saveButton: {
    flex: 1,
    paddingVertical: ms(12),
    borderRadius: ms(8),
    backgroundColor: '#29BFC0',
    alignItems: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#CCCCCC',
  },
  saveButtonText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
